// Figma file: https://www.figma.com/design/RLAwR3rK22mTGAOYui5uSQ/Web-Structure?node-id=XXX-XXX&m=dev

import {
  ComponentProps,
  ComponentPropsWithoutRef,
  forwardRef,
  ReactNode,
  TextareaHTMLAttributes,
  useContext,
} from "react"
import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { type VariantProps } from "class-variance-authority"

import AlertTriangle from "@/assets/icons/alertTriangle/medium.svg?react"
import HelpingHandTooltip from "@/components/shared/custom-tooltips/HelpingHandTooltip"
import InReviewModeContext from "@/components/ui/InReviewModeContext"
import { cn } from "@/lib/utils"

import {
  inputIconContainerVariants,
  inputLabelVariants,
  type InputVariants,
  inputVariants,
  inputWrapperVariants,
} from "./consts"

// Error Message Component
const ErrorMessage = ({ error, className }: { error?: string; className?: string }) =>
  error && (
    <div
      className={cn(
        "overflow-hidden text-sm leading-[1em] text-danger-primary transition-all duration-500",
        error ? "mt-1 h-[1.5em]" : "mt-0 h-0",
        className,
      )}
    >
      <div className="flex flex-row items-center gap-2">
        <AlertTriangle className="[&>*]-fill-danger-primary h-3 w-3" />
        <div>{error}</div>
      </div>
    </div>
  )

// Label Component
const Label = ({
  children,
  className,
  label,
  tooltip,
}: {
  children: ReactNode
  className?: string
  label: ReactNode
  tooltip?: ReactNode
}) => {
  const inReview = useContext(InReviewModeContext)

  return (
    <label className={cn("block transition-all duration-300", inReview && "pb-4", className)}>
      <span className={inputLabelVariants({ inReview })}>
        {label}
        {tooltip && <HelpingHandTooltip className="ml-1">{tooltip}</HelpingHandTooltip>}
      </span>
      {children}
    </label>
  )
}

// Input Wrapper Component
export interface InputWrapperProps {
  label?: ReactNode
  tooltip?: ReactNode
  error?: string
  classNameWrapper?: string | boolean
  children?: ReactNode
}

export const InputWrapper = ({ classNameWrapper = true, label, tooltip, error, children }: InputWrapperProps) => {
  const content = (
    <>
      {label ? (
        <Label label={label} tooltip={tooltip}>
          {children}
        </Label>
      ) : (
        children
      )}
      <ErrorMessage error={error} />
    </>
  )

  return classNameWrapper ? (
    <div className={typeof classNameWrapper === "string" ? classNameWrapper : undefined}>{content}</div>
  ) : (
    content
  )
}

// Base Input Props
export type InputBaseProps = ComponentProps<"input"> &
  VariantProps<typeof inputVariants> & {
    rightIcon?: ReactNode
    leftIcon?: ReactNode
  }

// Base Input Component
export const InputBase = forwardRef<HTMLInputElement, InputBaseProps>(
  ({ className, disabled, inReview, variant, inputSize, leftIcon, rightIcon, ...props }, ref) => (
    <div className="relative flex items-center">
      {leftIcon && <span className="absolute left-3">{leftIcon}</span>}
      <input
        ref={ref}
        className={cn(
          inputVariants({
            inReview,
            variant: inReview ? "primary" : variant,
            inputSize,
          }),
          leftIcon && "pl-10",
          rightIcon && "pr-10",
          disabled && "pointer-events-none border-neutral-disabled bg-neutral-border opacity-50",
          className,
        )}
        disabled={disabled}
        readOnly={inReview || undefined}
        {...props}
      />
      {rightIcon && <span className="absolute right-3">{rightIcon}</span>}
    </div>
  ),
)
InputBase.displayName = "InputBase"

// Full Input Props
export type InputProps = InputBaseProps & InputWrapperProps

// Main Input Component
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, tooltip, error, variant, classNameWrapper, ...props }, ref) => (
    <InputWrapper classNameWrapper={classNameWrapper} label={label} tooltip={tooltip} error={error}>
      <InputBase ref={ref} variant={error ? "error" : variant} {...props} />
    </InputWrapper>
  ),
)
Input.displayName = "Input"

// Form Field Component
export const InputFormField = (props: Omit<ComponentPropsWithoutRef<typeof Input>, "children"> & { name: string }) => {
  const inReview = useContext(InReviewModeContext)
  const { t } = useTranslation()

  return (
    <Controller
      name={props.name}
      render={({ field, fieldState: { error } }) => (
        <Input
          {...field}
          value={field.value ?? ""}
          onChange={(e) => {
            const val = e.target.value
            field.onChange(props.type === "number" ? (val === "" ? "" : Number(val)) : val)
          }}
          error={error?.message}
          inReview={inReview}
          placeholder={props.placeholder ?? (inReview ? t("noDataYet") : props.placeholder)}
          {...props}
        />
      )}
    />
  )
}

// Replace Form Field Component (for custom display in review mode)
export const InputReplaceFormField = ({
  children,
  name,
  placeholder,
  className,
  ...props
}: {
  name: string
  placeholder?: string
  children: (value: string) => ReactNode
} & Omit<InputProps, "name" | "children">) => {
  const { watch } = useFormContext()
  const inReview = useContext(InReviewModeContext)
  const { t } = useTranslation()

  const value = watch(name)

  return (
    <div className="relative">
      <InputFormField
        name={name}
        className={cn(className, inReview && "pointer-events-none opacity-0")}
        inReview={inReview}
        placeholder={placeholder}
        {...props}
      />

      <div
        className={cn(
          inputVariants({ inReview }),
          "absolute left-0 top-0",
          !inReview && "pointer-events-none opacity-0",
        )}
      >
        {value !== undefined && value !== null && value !== "" ? (
          children(value)
        ) : (
          <span className="text-neutral-text">{placeholder ?? t("noDataYet")}</span>
        )}
      </div>
    </div>
  )
}

// Textarea Base Props
interface TextareaBaseProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string
  disabled?: boolean
  inReview?: boolean
  variant?: "primary" | "error" | null | undefined
  inputSize?: "sm" | "md" | null | undefined
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

// Textarea Base Component
const TextareaBase = forwardRef<HTMLTextAreaElement, TextareaBaseProps>(
  ({ className, disabled, inReview, variant, inputSize, leftIcon, rightIcon, ...props }, ref) => (
    <div className="relative flex items-center">
      {leftIcon && <span className="absolute left-3">{leftIcon}</span>}
      <textarea
        ref={ref}
        className={cn(
          inputVariants({
            inReview,
            variant: inReview ? "primary" : variant,
            inputSize,
          }),
          leftIcon && "pl-10",
          rightIcon && "pr-10",
          disabled && "pointer-events-none border-neutral-disabled bg-neutral-border opacity-50",
          className,
        )}
        disabled={disabled}
        readOnly={inReview}
        {...props}
      />
      {rightIcon && <span className="absolute right-3">{rightIcon}</span>}
    </div>
  ),
)
TextareaBase.displayName = "TextareaBase"

// Textarea Props
export type TextareaProps = TextareaBaseProps & InputWrapperProps

// Textarea Component
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, tooltip, error, variant, classNameWrapper, ...props }, ref) => (
    <InputWrapper classNameWrapper={classNameWrapper} label={label} tooltip={tooltip} error={error}>
      <TextareaBase ref={ref} variant={error ? "error" : variant} {...props} />
    </InputWrapper>
  ),
)
Textarea.displayName = "Textarea"
